import { useEffect, useState } from "react";
import { ChevronRight, Download, Eye, Image as ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";
import client, { absoluteUrl } from "../api/client";
import { capitalise } from "../utils/text";
import { useAuth } from "../context/AuthContext";
import "./Dashboard.css";

/** Portal overview: headline counts, library breakdown, and most-viewed art. */

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US").format(Number(value) || 0);

export function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [mostViewed, setMostViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotice("");

      const [statsResult, viewedResult] = await Promise.allSettled([
        client.get("/api/dashboard/stats"),
        client.get("/api/images", {
          params: { sort: "views", page_size: 8 },
        }),
      ]);

      if (statsResult.status === "fulfilled") {
        setStats(statsResult.value.data);
      } else {
        console.error("Stats failed:", statsResult.reason);
        setNotice("Portal statistics are temporarily unavailable.");
      }

      if (viewedResult.status === "fulfilled") {
        setMostViewed(viewedResult.value.data.items || []);
      } else {
        console.error("Most viewed failed:", viewedResult.reason);
        setMostViewed([]);
      }

      setLoading(false);
    };

    load();
  }, []);

  if (loading) {
    return (
      <main className="dashboard-shell">
        <div className="dashboard-state">Loading dashboard...</div>
      </main>
    );
  }

  const tiles = [
    { label: "Total assets", value: stats?.total_assets, icon: ImageIcon },
  ];

  if (isAdmin) {
    tiles.push({
      label: "Total downloads",
      value: stats?.total_downloads,
      icon: Download,
    });
  }

  return (
    <main className="dashboard-shell">
      {notice && <div className="dashboard-notice">{notice}</div>}

      {user?.name && (
        <p className="dashboard-greeting">Welcome back, {user.name}.</p>
      )}

      <section className="dashboard-summary" aria-label="Portal summary">
        {tiles.map(({ label, value, icon: Icon, to }) => {
          const content = (
            <>
              <Icon size={18} strokeWidth={1.7} aria-hidden="true" />
              <div>
                <p className="dashboard-kicker">{label}</p>
                <strong>{formatNumber(value)}</strong>
              </div>
            </>
          );

          return to ? (
            <Link className="dashboard-stat is-link" to={to} key={label}>
              {content}
            </Link>
          ) : (
            <div className="dashboard-stat" key={label}>
              {content}
            </div>
          );
        })}
      </section>

      {Boolean(stats?.by_category?.length) && (
        <section className="dashboard-categories" aria-label="Assets by collection">
          <div className="dashboard-section-heading">
            <div>
              <p className="dashboard-kicker">Library</p>
              <h2>By collection</h2>
            </div>
          </div>
          <div className="dashboard-category-grid">
            {stats.by_category.map((entry) => (
              <Link
                className="dashboard-category"
                to={`/All-Images?category=${encodeURIComponent(
                  String(entry.label).toLowerCase()
                )}`}
                key={entry.label}
              >
                <span className="dashboard-category-name">
                  {capitalise(entry.label)}
                </span>
                <span className="dashboard-category-value">
                  <strong>{formatNumber(entry.count)}</strong>
                  <ChevronRight size={14} strokeWidth={1.8} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="recently-viewed-section" aria-label="Most viewed artwork">
        <div className="dashboard-section-heading">
          <div>
            <p className="dashboard-kicker">Activity</p>
            <h2>Most Viewed</h2>
          </div>
          <Link to="/All-Images">Browse archive</Link>
        </div>

        {mostViewed.length ? (
          <div className="recently-viewed-grid">
            {mostViewed.map((asset) => {
              const imageUrl = absoluteUrl(asset.thumbnail_path);
              const title = capitalise(asset.file_name) || "Untitled artwork";

              return (
                <article className="recently-viewed-card" key={asset.file_id}>
                  <div className="recently-viewed-image">
                    {imageUrl ? (
                      <img src={imageUrl} alt={title} loading="lazy" />
                    ) : (
                      <ImageIcon size={26} strokeWidth={1.5} aria-hidden="true" />
                    )}
                  </div>
                  <div className="recently-viewed-copy">
                    <p>{title}</p>
                    <span>
                      <Eye size={14} strokeWidth={1.7} aria-hidden="true" />
                      {formatNumber(asset.views)} views
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="dashboard-empty">No viewed artwork yet.</div>
        )}
      </section>
    </main>
  );
}

export default Dashboard;
